// noinspection DuplicatedCode

import { AxiosInstance, default as axiosInstance, InternalAxiosRequestConfig } from 'axios';
import { notification } from 'antd';
import { MyAuth0Context } from 'index';
import { useContext, useEffect, useMemo, useRef } from 'react';

export const useAxios = (baseURL: string): { axios: AxiosInstance } => {
  // Using MyAuth0Context instead of useAuth0 hook directly since we need the wrapped context
  const { getAccessTokenSilently } = useContext(MyAuth0Context);
  const getTokenRef = useRef(getAccessTokenSilently);
  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  const axios = useMemo(() => {
    const instance = axiosInstance.create({ baseURL });

    instance.interceptors.request.use(async (config: InternalAxiosRequestConfig<any>) => {
      try {
        const token = await getTokenRef.current();
        if (config.headers) {
          config.headers.Accept = 'application/json,text/plain';
          // Let the browser set multipart boundary for file uploads; forcing JSON breaks POST FormData.
          if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
          }
          config.headers['Cache-Control'] = 'no-cache';

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      } catch (err) {
        // return original config if JSON.parse fails
        return config;
      }
    });

    instance.interceptors.response.use(undefined, (error: any) => {
      const status = error.response ? error.response.status : null;
      if (status === 403) {
        notification.warning({
          message: 'Protected Data',
          description: 'You do not have permission to access this information.',
          duration: 8
        });
        return error;
      } else {
        const message = error.response?.data?.message;
        notification.warning({
          message: 'Error',
          description: Array.isArray(message) ? message[0] : message,
          duration: 8
        });
        return error;
      }
    });

    return instance;
  }, [baseURL]);

  return { axios };
};
